import { supabase } from '@/lib/supabase';

export interface NotificationTemplate {
  id: string;
  notification_type: string;
  title_template: string;
  message_template: string;
  email_subject_template?: string;
  email_body_template?: string;
  sms_template?: string;
  slack_template?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  description?: string;
  variables?: string[];
}

/**
 * Maps actual data field names to template variable names for better compatibility
 */
function normalizeVariablesForTemplate(variables: Record<string, any>, notificationType: string): Record<string, any> {
  const normalized = { ...variables };
  
  // Common field mappings
  const fieldMappings: Record<string, string[]> = {
    // Map sender_name to commenter_name for comment-related templates
    'commenter_name': ['sender_name', 'mentioned_by', 'updated_by', 'created_by', 'assigned_by'],
    
    // Map various context fields to 'context'
    'context': ['project_name', 'ticket_title', 'task_name', 'organization_name'],
    
    // Map comment content fields
    'comment_text': ['message_content', 'comment_content', 'content', 'message'],
    
    // Map mentioned user fields
    'mentioned_by': ['sender_name', 'commenter_name'],
    
    // Map created_by variations
    'created_by': ['created_by_user', 'sender_name'],
    
    // Specific mappings for different notification types
    'task_name': ['title', 'name'],
    'ticket_id': ['id'],
    'project_type': ['type'],
    'organization_type': ['type'],
    
    // Task-specific mappings
    'task_description': ['description'],
    'due_date': ['dueDate', 'due_date'],
    'completion_date': ['completedAt', 'completed_at', 'completion_date'],
    
    // Ticket-specific mappings
    'ticket_title': ['title'],
    'priority': ['priority'],
    'status': ['status'],
  };
  
  // Apply field mappings
  for (const [templateVar, sourceFields] of Object.entries(fieldMappings)) {
    if (!normalized[templateVar]) {
      // Find the first available source field and use its value
      for (const sourceField of sourceFields) {
        if (normalized[sourceField] !== undefined && normalized[sourceField] !== null) {
          normalized[templateVar] = normalized[sourceField];
          break;
        }
      }
    }
  }
  
  // Type-specific mappings
  if (notificationType.includes('comment') || notificationType.includes('mention')) {
    // For comment/mention notifications, ensure we have context
    if (!normalized['context']) {
      if (normalized['project_name']) {
        normalized['context'] = `project "${normalized['project_name']}"`;
      } else if (normalized['ticket_title']) {
        normalized['context'] = `ticket "${normalized['ticket_title']}"`;
      } else if (normalized['task_name']) {
        normalized['context'] = `task "${normalized['task_name']}"`;
      } else if (normalized['organization_name']) {
        normalized['context'] = `organization "${normalized['organization_name']}"`;
      } else {
        normalized['context'] = 'the item';
      }
    }
  }
  
  // Ensure we have commenter_name for comment notifications  
  if (notificationType === 'comment' && !normalized['commenter_name']) {
    normalized['commenter_name'] = normalized['sender_name'] || 'Someone';
  }
  
  return normalized;
}

/**
 * Fetch the notification template for a given type and channel.
 * @param type Notification type (e.g., 'project_created')
 * @param channel Channel (e.g., 'email', 'in_app', 'sms', 'slack')
 * @returns The template object or null if not found/active
 */
export async function getNotificationTemplate(type: string, channel: string): Promise<NotificationTemplate | null> {
  // Get current tenant ID
  const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
  if (tenantError) {
    console.error('[notificationTemplateService] Error getting tenant ID:', tenantError);
    return null;
  }

  const { data, error } = await supabase
    .from('notification_templates')
    .select('*')
    .eq('notification_type', type)
    .eq('is_active', true)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) {
    console.error('[notificationTemplateService] Error fetching template:', error);
    return null;
  }
  if (!data) return null;
  // Optionally, check that the template has content for the requested channel
  const channelField = getChannelField(channel);
  if (!data[channelField]) return null;
  return data as NotificationTemplate;
}

function getChannelField(channel: string): keyof NotificationTemplate {
  switch (channel) {
    case 'email': return 'email_body_template';
    case 'in_app': return 'message_template';
    case 'sms': return 'sms_template';
    case 'slack': return 'slack_template';
    default: return 'message_template';
  }
}

/**
 * Render a template string by replacing {variables} with values from the variables object.
 * @param template The template string (e.g., 'Hello {name}')
 * @param variables Object with variable values (e.g., { name: 'John' })
 * @param notificationType The notification type for context-aware mapping
 * @returns The rendered string
 */
export function renderTemplate(template: string, variables: Record<string, any>, notificationType?: string): string {
  if (!template) return '';
  
  // Normalize variables for better template compatibility
  const normalizedVariables = notificationType 
    ? normalizeVariablesForTemplate(variables, notificationType)
    : variables;
  
  // Rendering template with normalized variables
  
  const rendered = template.replace(/\{(\w+)\}/g, (match, variable) => {
    const value = normalizedVariables[variable];
    if (value !== undefined && value !== null) {
      // Special handling for custom questions - if empty, return empty string instead of placeholder
      if ((variable === 'custom_questions_html' || variable === 'custom_questions_text') && !value.toString().trim()) {
        return '';
      }
      return String(value);
    }
    // For custom questions variables, return empty string if not available
    if (variable === 'custom_questions_html' || variable === 'custom_questions_text' || variable === 'has_custom_questions') {
      return '';
    }
    console.warn('[renderTemplate] Missing template variable:', {
      variable,
      match,
      template: template.substring(0, 50) + '...',
      availableVariables: Object.keys(normalizedVariables),
      notificationType
    });
    return match; // Keep the original placeholder if no value found
  });
  
  // Template rendered successfully
  
  return rendered;
} 